// react
import { useDispatch } from 'react-redux';

// internationalization
import { useTranslation } from 'react-i18next';

// material ui
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';

// api
import { useGatewaysDeleteAsyncByidMutation } from '@/api/generated/recalc/endpoints/v1/gateways';

// components
import { showSnackbar } from '@/redux/snackbarSlice';

interface Gateway {
  id?: string;
  GatewayId: number;
  Name?: string;
}

interface DeleteGatewayDialogProps {
  open: boolean;
  gateway: Gateway | null;
  onClose: () => void;
  /** Called after a successful delete, e.g. to navigate away. */
  onDeleted?: (gateway: Gateway) => void;
}

const DeleteGatewayDialog = ({
  open,
  gateway,
  onClose,
  onDeleted,
}: DeleteGatewayDialogProps) => {
  const { t } = useTranslation('units-gateways-sensors');
  const dispatch = useDispatch();
  const [deleteGateway, { isLoading: isDeleting }] =
    useGatewaysDeleteAsyncByidMutation();

  const handleConfirm = async () => {
    if (!gateway?.GatewayId) return;

    const result = await deleteGateway({ id: gateway.GatewayId });

    if (!('error' in result)) {
      dispatch(
        showSnackbar({
          message: t('gateways.gatewayDeletedSuccess', {
            name: gateway.Name || gateway.GatewayId,
          }),
          severity: 'success',
        }),
      );
      onClose();
      onDeleted?.(gateway);
    }
    // Errors are handled globally by apiErrorMiddleware (snackbar + App Insights).
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('gateways.deleteGateway')}</DialogTitle>
      <DialogContent dividers>
        <Typography>{t('gateways.deleteConfirmation')}</Typography>
        <Typography sx={{ mt: 2, fontWeight: 'bold' }}>
          {gateway?.Name || gateway?.GatewayId}
        </Typography>
        {gateway?.GatewayId && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {t('gateways.gatewayId')}: {gateway.GatewayId}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isDeleting}>
          {t('common.cancel')}
        </Button>
        <Button
          onClick={handleConfirm}
          color="error"
          variant="contained"
          disabled={isDeleting}
        >
          {isDeleting ? t('common.deleting') : t('common.delete')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DeleteGatewayDialog;
